import { useState } from 'react';
import type { SyncDomainSpec } from '../../marketconnect/framework';
import { ProgressFeedback } from '../motionxp/ProgressFeedback';
import { Button } from '../ui/Button';
import { cn } from '../../lib/cn';

type SyncJob = { id: string; domain: string; mode: string; state: string; progress: number };

type Props = {
  domains: SyncDomainSpec[];
  jobs?: SyncJob[];
};

export function SyncCommandCenter({ domains, jobs = [] }: Props) {
  const [busy, setBusy] = useState(false);

  const stats = {
    active: jobs.filter((j) => j.state === 'running').length,
    queued: jobs.filter((j) => j.state === 'queued').length,
    failed: jobs.filter((j) => j.state === 'failed').length,
    completed: jobs.filter((j) => j.state === 'completed').length,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 text-sm">
        <span>Active: <strong>{stats.active}</strong></span>
        <span>Queued: <strong>{stats.queued}</strong></span>
        <span className="text-error-600">Failed: <strong>{stats.failed}</strong></span>
        <span className="text-success-600">Completed: <strong>{stats.completed}</strong></span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" size="sm" disabled={busy} onClick={() => setBusy(true)}>Full Sync</Button>
        <Button variant="secondary" size="sm">Pause All</Button>
        <Button variant="secondary" size="sm">Resume</Button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {domains.map((d) => (
          <button
            key={d.domain}
            type="button"
            className={cn(
              'rounded-lg border p-3 text-left text-sm transition hover:bg-slate-50 dark:hover:bg-slate-800',
              d.default ? 'border-brand-200 dark:border-brand-800' : 'border-slate-200 dark:border-slate-700',
            )}
          >
            <p className="font-medium">{d.label}</p>
            {d.priority && <p className="text-xs capitalize text-slate-500">{d.priority} priority</p>}
          </button>
        ))}
      </div>

      {jobs.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active Jobs</p>
          <ul className="mt-2 space-y-3">
            {jobs.filter((j) => j.state === 'running' || j.state === 'failed').map((j) => (
              <li key={j.id}>
                <div className="flex justify-between text-sm">
                  <span className="capitalize">{j.domain} ({j.mode})</span>
                  <span className={cn(
                    'text-xs font-medium capitalize',
                    j.state === 'failed' ? 'text-error-600' : 'text-brand-600',
                  )}>
                    {j.state}
                  </span>
                </div>
                {j.state === 'running' && <ProgressFeedback value={j.progress} showPercent={false} className="mt-1" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
